import { useState, type ChangeEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { publicUrl } from '@/lib/env';
import {
  IMAGE_MAX_BYTES,
  base64FromDataUrl,
  readFileAsDataUrl,
  validateImageFile,
} from '@/lib/image-file';
import { useUploadSizeChart } from './use-settings';
import { GUIA_TALLAS_MAX_FOTOS } from './settings-validation';

/**
 * Guía de tallas del negocio (Ajustes → General, 24-sep-2026): subir **hasta DOS imágenes** desde el
 * computador del admin o pegar sus direcciones.
 *
 * Son de todo el NEGOCIO (no de un producto): los comercios que manejan tabla de tallas (calzado, ropa)
 * las cargan una vez —por ejemplo la del calzado y la de la ropa— y el bot las manda juntas cuando el
 * cliente pregunta por tallas («¿qué talla me queda?», «mándame la tabla de tallas»), sin pasar por la IA.
 * El orden de la lista es el orden en que se envían.
 *
 * La subida reutiliza `POST .../products/fotos` (ver `useUploadSizeChart` en `api/settings.ts`) y devuelve
 * la URL, que entra al formulario y se persiste con «Guardar cambios» (`PATCH` de `settings`). Igual que
 * el logo: descartar el formulario no cambia nada, y quitar una imagen solo la saca de la lista (el
 * archivo queda en disco sin usar hasta que se guarde).
 */
export interface SettingsSizeChartFieldProps {
  /** `guiaTallasFotos` del formulario (las guardadas + las que acaba de devolver la subida). */
  value: string[];
  onChange: (fotos: string[]) => void;
  /** `true` sin rol ADMIN: el `PATCH` y la subida exigen ADMIN. */
  disabled?: boolean;
  /** Mensaje de validación del formulario (se pinta bajo la lista). */
  error?: string;
}

export function SettingsSizeChartField({
  value,
  onChange,
  disabled = false,
  error,
}: SettingsSizeChartFieldProps) {
  const { tenantId } = useActiveTenantAccess();
  const uploadSizeChart = useUploadSizeChart(tenantId);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  /** URL pegada a mano que todavía no se agregó a la lista. */
  const [nuevaUrl, setNuevaUrl] = useState('');
  /** Cambiar la `key` permite volver a elegir el MISMO archivo después de quitarlo. */
  const [fileInputKey, setFileInputKey] = useState(0);

  const fotos = value.map((foto) => foto.trim()).filter((foto) => foto.length > 0);
  const completas = fotos.length >= GUIA_TALLAS_MAX_FOTOS;
  const ocupado = disabled || uploading;
  const maxMb = Math.round(IMAGE_MAX_BYTES / 1024 / 1024);

  function agregar(url: string) {
    const limpia = url.trim();
    if (limpia.length === 0 || completas) return;
    setUploadError(null);
    onChange([...fotos, limpia]);
  }

  function quitar(index: number) {
    setUploadError(null);
    onChange(fotos.filter((_foto, posicion) => posicion !== index));
  }

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setFileInputKey((key) => key + 1);
    if (!file) return;

    const fileError = validateImageFile(file);
    if (fileError) {
      setUploadError(fileError);
      return;
    }

    setUploadError(null);
    setUploading(true);

    try {
      const { base64, mimeType } = base64FromDataUrl(await readFileAsDataUrl(file));
      const saved = await uploadSizeChart.mutateAsync({
        imageBase64: base64,
        ...(mimeType ? { mimeType } : {}),
      });
      agregar(saved.url);
    } catch (err) {
      setUploadError(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-4">
      {uploadError ? <Alert tone="danger">{uploadError}</Alert> : null}

      {/* Las imágenes cargadas, en el orden en que el bot las va a mandar. */}
      {fotos.length > 0 ? (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {fotos.map((url, index) => (
            <li
              key={url}
              className="space-y-1 rounded-control border border-line bg-surface p-1"
            >
              <img
                src={publicUrl(url)}
                alt={`Imagen ${index + 1} de la guía de tallas`}
                loading="lazy"
                className="h-24 w-full rounded-control bg-surface-muted object-cover"
              />
              <span className="block px-1 text-micro text-ink-faint">
                Imagen {index + 1} de {GUIA_TALLAS_MAX_FOTOS}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                disabled={ocupado}
                onClick={() => quitar(index)}
              >
                Quitar la imagen {index + 1}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {completas ? (
        <p className="text-caption text-ink-muted">
          Ya están las {GUIA_TALLAS_MAX_FOTOS} imágenes. Quitá una para cambiarla: el cliente las recibe en
          este orden.
        </p>
      ) : (
        <>
          <Field
            label={
              fotos.length === 0
                ? 'Subir la guía de tallas desde tu computador'
                : `Subir la imagen ${fotos.length + 1}`
            }
            htmlFor="guiaTallasArchivo"
            hint={`PNG, JPG, WEBP, GIF o HEIC de hasta ${maxMb} MB. Hasta ${GUIA_TALLAS_MAX_FOTOS} imágenes por negocio (ej. la tabla del calzado y la de la ropa); se suben al elegirlas y se guardan al pulsar «Guardar cambios».`}
          >
            <Input
              key={fileInputKey}
              id="guiaTallasArchivo"
              name="guiaTallasArchivo"
              type="file"
              accept="image/*"
              disabled={ocupado}
              onChange={(event) => void handleFile(event)}
            />
          </Field>

          <div className="flex flex-wrap items-end gap-3">
            <Field
              label="…o pegar la dirección de una imagen (URL)"
              htmlFor="guiaTallasUrl"
              className="min-w-64 flex-1"
              hint="Ej. https://tunegocio.com/tabla-de-tallas.png"
              error={error}
            >
              <Input
                id="guiaTallasUrl"
                name="guiaTallasUrl"
                type="url"
                placeholder="https://…"
                value={nuevaUrl}
                disabled={ocupado}
                invalid={Boolean(error)}
                onChange={(event) => setNuevaUrl(event.target.value)}
              />
            </Field>

            <Button
              variant="secondary"
              disabled={ocupado || nuevaUrl.trim().length === 0}
              onClick={() => {
                agregar(nuevaUrl);
                setNuevaUrl('');
              }}
            >
              {fotos.length === 0 ? 'Agregar la imagen' : `Agregar la imagen ${fotos.length + 1}`}
            </Button>
          </div>
        </>
      )}

      {fotos.length > 0 ? (
        <p className="text-caption text-ink-faint">
          El cliente recibe {fotos.length === 1 ? 'esta imagen' : 'estas dos imágenes'} apenas pregunta por
          tallas, junto con un texto que menciona el negocio.
        </p>
      ) : (
        <p className="text-caption text-ink-faint">
          Sin guía cargada, el bot responde las preguntas de tallas como cualquier otra (con el catálogo).
        </p>
      )}
    </div>
  );
}
